import * as React from "react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CalendarIcon, X } from "lucide-react";
import { format, startOfDay } from "date-fns";
import { useLingui } from "@lingui/react";
import { getDateFnsLocale, type Locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { formatDateDisplay } from "@/lib/format";
import { parseTypedDate } from "@/utils/parseTypedDate";

export interface DatePickerProps {
  value?: Date | string;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  "aria-describedby"?: string;
  /**
   * Accessible name for the text input. Only used when the picker is NOT bound
   * to an external `<label htmlFor>` — i.e. when no `id` is supplied — because
   * `aria-label` overrides a visible label rather than supplementing it.
   */
  "aria-label"?: string;
  /**
   * Error state reported by the surrounding form (e.g. `FormControl`). Sets
   * `aria-invalid` on the text input in addition to the picker's own parse
   * state; the red border stays tied to the parse state only.
   */
  "aria-invalid"?: boolean | "true" | "false";
  /** Required state reported by the surrounding form (`FormLabel required`). */
  "aria-required"?: boolean | "true" | "false";
  /** Test id forwarded to the text input. */
  "data-testid"?: string;
  showClearButton?: boolean;
  /**
   * Earliest selectable day (inclusive). Days before it are disabled in the
   * calendar and typed input that resolves before it is rejected as invalid.
   * Compared at day granularity, so a `minDate` carrying a time-of-day still
   * allows that whole calendar day.
   */
  minDate?: Date;
}

// Ctrl/Cmd chords that edit the text itself, never a shortcut.
const TEXT_EDITING_CHORD_KEYS = new Set(["a", "c", "v", "x", "y", "z"]);

/** An Alt/Ctrl/Cmd chord on a printable key that an ancestor may handle. */
function isShortcutChord(e: React.KeyboardEvent): boolean {
  if (e.key.length !== 1) return false;
  if (e.altKey) return true;
  return (e.ctrlKey || e.metaKey) && !TEXT_EDITING_CHORD_KEYS.has(e.key.toLowerCase());
}

/**
 * Typed text reaches `onChange` only when it is committed, on blur or Enter.
 * Shorthand makes every prefix of a date a date of its own: `1.7.2026` passes
 * `1.7` (this year) and `1.7.20` (2020), so a per-keystroke emit would hand
 * consumers dates the user never meant. Calendar picks and the clear button
 * report immediately. Enter and Alt/Ctrl/Cmd shortcut chords commit first, so
 * a form submit or an ancestor's keyboard shortcut sees the typed date.
 *
 * ⚠️ TIMEZONE WARNING: This DatePicker creates Date objects at LOCAL midnight.
 *
 * When serializing a date-only value for an API, DO NOT use toISOString(). It
 * converts to UTC, and in any zone AHEAD of UTC that rolls the calendar day
 * BACKWARDS: 2026-05-01 00:00 in Europe/Zurich (UTC+2) becomes
 * "2026-04-30T22:00:00.000Z" — the wrong date, stored silently.
 *
 * Use `formatLocalDate(date)` from `@/utils/dateUtils` → "2026-05-01".
 *
 * If the endpoint's Go DTO binds the field as `time.Time` (a bare date 400s),
 * use `toUtcMidnightTimestamp(date)` from the same module instead.
 */

export const DatePicker = React.forwardRef<HTMLInputElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      placeholder,
      disabled,
      className,
      id,
      "aria-describedby": ariaDescribedBy,
      "aria-label": ariaLabel,
      "aria-invalid": ariaInvalid,
      "aria-required": ariaRequired,
      "data-testid": dataTestId,
      showClearButton = true,
      minDate,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [isInvalid, setIsInvalid] = React.useState(false);
    const { i18n } = useLingui();

    // Get the current date-fns locale based on Lingui locale
    const dateFnsLocale = getDateFnsLocale(i18n.locale as Locale);

    const resolvedPlaceholder =
      placeholder ??
      i18n._({ id: "common.datePicker.placeholder", message: "Select date" });

    // An `aria-label` REPLACES an associated `<label>` rather than adding to
    // it, so only name the input here when nothing else does. Inside a
    // `FormControl` the picker receives an `id` that a `<label htmlFor>`
    // points at — naming it again would announce "Date input" in place of the
    // field's real label.
    const inputAriaLabel = ariaLabel
      ? ariaLabel
      : id
        ? undefined
        : i18n._({ id: "common.datePicker.inputLabel", message: "Date input" });

    // Normalize value to a stable timestamp for comparison
    const valueTimestamp = React.useMemo(() => {
      if (!value) return null;
      if (value instanceof Date) return isNaN(value.getTime()) ? null : value.getTime();
      const parsed = new Date(value as string);
      return isNaN(parsed.getTime()) ? null : parsed.getTime();
    }, [value]);

    // Ensure we have a proper Date object for the picker
    const dateValue = React.useMemo(() => {
      if (valueTimestamp === null) return null;
      return new Date(valueTimestamp);
    }, [valueTimestamp]);

    // Track the current month being displayed (separate from selected date)
    const [displayMonth, setDisplayMonth] = React.useState(
      () => dateValue || new Date()
    );

    // Update input value when value prop changes
    React.useEffect(() => {
      if (dateValue) {
        setInputValue(formatDateDisplay(dateValue));
        setDisplayMonth(dateValue);
        setIsInvalid(false);
      } else {
        setInputValue("");
        setDisplayMonth(new Date());
        setIsInvalid(false);
      }
    }, [dateValue]);

    // Parse date from input string
    const minDay = React.useMemo(
      () => (minDate ? startOfDay(minDate) : null),
      [minDate]
    );

    const parseDate = (input: string): Date | null =>
      parseTypedDate(input, new Date(), {
        minDate: minDay ?? undefined,
      });

    // Typing only updates the text and the live invalid state; the value is
    // reported on commit (blur or Enter).
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInputValue(newValue);

      if (!newValue.trim()) {
        setIsInvalid(false);
        return;
      }

      const parsed = parseDate(newValue);
      if (parsed) {
        setDisplayMonth(parsed);
        setIsInvalid(false);
      } else {
        setIsInvalid(true);
      }
    };

    // Resolve the typed text and normalise it to the display format. Shared by
    // blur and Enter. Returns whether the text was valid or empty.
    const commitInputText = (): boolean => {
      if (!inputValue.trim()) {
        onChange?.(undefined);
        setIsInvalid(false);
        return true;
      }

      const parsed = parseDate(inputValue);
      if (parsed) {
        setInputValue(formatDateDisplay(parsed));
        onChange?.(parsed);
        setDisplayMonth(parsed);
        setIsInvalid(false);
        return true;
      }
      setIsInvalid(true);
      return false;
    };

    const handleInputBlur = () => {
      commitInputText();
    };

    // Enter and shortcut chords commit like blur, before the key reaches the
    // surrounding form or an ancestor shortcut handler (a wizard's Alt+N would
    // otherwise advance and unmount the field with the typed date uncommitted).
    // A valid or empty text lets the key through; an invalid text consumes it,
    // so nothing submits or navigates on the last committed value while the
    // field shows different text.
    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key !== "Enter" && !isShortcutChord(e)) return;
      if (!commitInputText()) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    // Handle calendar selection
    const handleCalendarSelect = (date: Date | undefined) => {
      onChange?.(date);
      setOpen(false);
      if (date) {
        setInputValue(formatDateDisplay(date));
        setDisplayMonth(date);
        setIsInvalid(false);
      }
    };

    // Handle clear button
    const handleClear = () => {
      onChange?.(undefined);
      setInputValue("");
      setIsInvalid(false);
      setDisplayMonth(new Date());
    };

    return (
      <div className="flex w-full gap-1">
        <div className="relative flex-1">
          <Input
            ref={ref}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onBlur={handleInputBlur}
            onKeyDown={handleInputKeyDown}
            placeholder={resolvedPlaceholder}
            disabled={disabled}
            className={cn(
              "pr-10",
              isInvalid && "border-destructive focus-visible:border-destructive",
              className
            )}
            id={id}
            aria-describedby={ariaDescribedBy}
            aria-invalid={
              isInvalid || ariaInvalid === true || ariaInvalid === "true"
            }
            aria-label={inputAriaLabel}
            aria-required={ariaRequired}
            data-testid={dataTestId}
          />
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-10 w-10 rounded-l-none hover:bg-transparent"
                disabled={disabled}
                aria-label={i18n._({
                  id: "common.datePicker.openCalendar",
                  message: "Open calendar",
                })}
              >
                <CalendarIcon className="h-4 w-4" aria-hidden="true" />
              </Button>
            </PopoverTrigger>
          <PopoverContent
            className="w-auto p-0 bg-popover border-border shadow-lg"
            align="start"
          >
            <div className="p-2 pb-0">
              <div className="flex space-x-2">
                <Select
                  value={displayMonth.getFullYear().toString()}
                  onValueChange={(year) => {
                    // Create new Date with explicit year, month, and day=1 to avoid overflow
                    const newMonth = new Date(parseInt(year), displayMonth.getMonth(), 1);
                    setDisplayMonth(newMonth);
                  }}
                >
                  <SelectTrigger
                    className="flex-1"
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label={i18n._({ id: "common.datePicker.year", message: "Year" })}
                  >
                    <SelectValue
                      placeholder={i18n._({
                        id: "common.datePicker.year",
                        message: "Year",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                    {Array.from({ length: 120 }, (_, i) => {
                      // Show 100 years in the past and 20 years in the future
                      const currentYear = new Date().getFullYear();
                      const year = currentYear - 100 + i;
                      return (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <Select
                  value={displayMonth.getMonth().toString()}
                  onValueChange={(month) => {
                    // Create new Date with explicit year, month, and day=1 to avoid overflow
                    const newMonth = new Date(displayMonth.getFullYear(), parseInt(month), 1);
                    setDisplayMonth(newMonth);
                  }}
                >
                  <SelectTrigger
                    className="flex-1"
                    onPointerDown={(e) => e.stopPropagation()}
                    aria-label={i18n._({ id: "common.datePicker.month", message: "Month" })}
                  >
                    <SelectValue
                      placeholder={i18n._({
                        id: "common.datePicker.month",
                        message: "Month",
                      })}
                    />
                  </SelectTrigger>
                  <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                    {Array.from({ length: 12 }, (_, i) => {
                      const date = new Date(2000, i, 1);
                      return (
                        <SelectItem key={i} value={i.toString()}>
                          {format(date, "MMMM", { locale: dateFnsLocale })}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Calendar
              mode="single"
              selected={dateValue || undefined}
              onSelect={handleCalendarSelect}
              disabled={minDay ? { before: minDay } : undefined}
              month={displayMonth}
              onMonthChange={setDisplayMonth}
              locale={dateFnsLocale}
              initialFocus
              classNames={{
                caption_label: "hidden",
                nav: "hidden",
              }}
            />
          </PopoverContent>
          </Popover>
        </div>
        {showClearButton && dateValue && !disabled && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="shrink-0"
            onClick={handleClear}
            aria-label={i18n._({
              id: "common.datePicker.clear",
              message: "Clear date",
            })}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </div>
    );
  }
);

DatePicker.displayName = "DatePicker";